<template>
  <v-form ref="form" v-model="valid" v-if="fund && fund.state === 'NEW' && formData && mayStartEdit">
    <v-stepper v-model="step" vertical flat>
      <v-stepper-items>
        <v-stepper-step step="1" :complete="step > 1">
          {{ $t("fundBaseForm.referenceType.title") }}
        </v-stepper-step>

        <v-stepper-content step="1">
          <div class="pa-1">
            <v-row>
              <v-col cols="6">
                <v-card>
                  <v-card-title>
                    {{ $t("fundBaseForm.referenceFund.description.commonTitle") }}
                  </v-card-title>

                  <v-card-text>
                    <Markdown :value="$t('fundBaseForm.referenceFund.description.common')"></Markdown>
                  </v-card-text>
                </v-card>
              </v-col>

              <v-col cols="6">
                <v-card>
                  <video
                    class="elementor-video"
                    :src="$t('fundBaseForm.referenceVideo.src')"
                    controlslist="nodownload"
                    controls
                    style="width: 100%; display: block"
                  ></video>
                </v-card>
              </v-col>

              <v-col v-if="oldFundsOptions.length > 0">
                <v-card>
                  <v-card-title>
                    {{ $t("fundBaseForm.referenceFund.description.oldTitle") }}
                  </v-card-title>

                  <v-card-text>
                    <Markdown :value="$t('fundBaseForm.referenceFund.description.old')"></Markdown>
                  </v-card-text>

                  <v-card-actions>
                    <v-btn
                      color="primary"
                      @click="
                        referenceType = 'old';
                        noReference = false;
                        formData.referenceFund = null;
                        step = 2;
                      "
                    >
                      {{ $t("fundBaseForm.referenceFund.oldReference") }}
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </v-col>

              <v-col v-if="currentFundsOptions.length > 0">
                <v-card>
                  <v-card-title>
                    {{ $t("fundBaseForm.referenceFund.description.currentTitle") }}
                  </v-card-title>

                  <v-card-text>
                    <Markdown :value="$t('fundBaseForm.referenceFund.description.current')"></Markdown>
                  </v-card-text>

                  <v-card-actions>
                    <v-btn
                      color="primary"
                      @click="
                        referenceType = 'current';
                        noReference = false;
                        formData.referenceFund = null;
                        step = 2;
                      "
                    >
                      {{ $t("fundBaseForm.referenceFund.currentReference") }}
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </v-col>

              <v-col>
                <v-card>
                  <v-card-title>
                    {{ $t("fundBaseForm.referenceFund.noReferenceTitle") }}
                  </v-card-title>

                  <v-card-text>
                    <Markdown :value="$t('fundBaseForm.referenceFund.noReferenceText')"></Markdown>
                  </v-card-text>

                  <v-card-actions>
                    <v-btn
                      color="error"
                      @click="
                        referenceType = 'none';
                        noReference = true;
                        formData.referenceFund = null;
                        step = 3;
                      "
                    >
                      {{ $t("fundBaseForm.referenceFund.noReference") }}
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </v-col>
            </v-row>
          </div>
        </v-stepper-content>

        <v-stepper-step step="2" :complete="step > 2">
          {{ $t("fundBaseForm.referenceFund.title") }}
          <small v-if="referenceType === 'none' && step > 2">{{ $t("fundBaseForm.referenceFund.none") }}</small>
        </v-stepper-step>

        <v-stepper-content step="2">
          <div class="pa-1">
            <v-card>
              <v-card-text>
                <v-autocomplete
                  v-model="formData.referenceFund"
                  :items="fundsOptions"
                  :label="$t('fundBaseForm.referenceFund.label')"
                  clearable
                >
                </v-autocomplete>
              </v-card-text>

              <v-card-actions>
                <v-btn
                  color="primary"
                  @click="
                    fill();
                    step = 3;
                  "
                  :disabled="!formData.referenceFund"
                >
                  {{ $t("fundBaseForm.referenceFund.fill") }}
                </v-btn>
              </v-card-actions>
            </v-card>
          </div>
        </v-stepper-content>

        <v-stepper-step step="3">
          {{ $t("fundBaseForm.title") }}
        </v-stepper-step>

        <v-stepper-content step="3">
          <div class="pa-1">
            <v-alert v-if="filled" type="info" dense>
              {{ $t("fundBaseForm.referenceFund.fillInfo") }}
            </v-alert>

            <v-alert v-if="!formData.referenceFund" type="error" dense>
              {{ $t("fundBaseForm.referenceFund.noReferenceFundInfo") }}
            </v-alert>

            <v-card>
              <v-card-text>
                <v-row>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-if="displayField('name')"
                      v-model="formData.name"
                      :label="$t('fundBaseForm.name.label')"
                      :rules="[(v) => !!v || $t('validation.required')]"
                      required
                    >
                      <template #prepend>
                        <v-tooltip bottom max-width="300">
                          <template #activator="{ on }">
                            <v-icon color="primary" dark v-on="on">mdi-help</v-icon>
                          </template>
                          <span>{{ $t("fundBaseForm.name.help") }}</span>
                        </v-tooltip>
                      </template>
                    </v-text-field>

                    <v-select
                      v-if="displayField('assetManagement')"
                      v-model="formData.assetManagement"
                      :items="assetManagement"
                      :label="$t('fundBaseForm.assetManagement.label')"
                    >
                      <template #prepend>
                        <v-tooltip bottom max-width="300">
                          <template #activator="{ on }">
                            <v-icon color="primary" dark v-on="on">mdi-help</v-icon>
                          </template>
                          <span>{{ $t("fundBaseForm.assetManagement.help") }}</span>
                        </v-tooltip>
                      </template>
                    </v-select>

                    <v-select
                      v-if="displayField('fof')"
                      :items="fof"
                      v-model="formData.fof"
                      :label="$t('fundBaseForm.fof.label')"
                    >
                      <template #prepend>
                        <v-tooltip bottom max-width="300">
                          <template #activator="{ on }">
                            <v-icon color="primary" dark v-on="on">mdi-help</v-icon>
                          </template>
                          <span>{{ $t("fundBaseForm.fof.help") }}</span>
                        </v-tooltip>
                      </template>
                    </v-select>

                    <v-autocomplete
                      v-if="displayField('assetClasses')"
                      v-model="formData.assetClasses"
                      :items="assetClasses"
                      :label="$t('fundBaseForm.assetClasses.label')"
                      :rules="[(v) => v.length > 0 || $t('validation.required')]"
                      multiple
                      small-chips
                      deletable-chips
                    >
                      <template #prepend>
                        <v-tooltip bottom max-width="300">
                          <template #activator="{ on }">
                            <v-icon color="primary" dark v-on="on">mdi-help</v-icon>
                          </template>
                          <span>{{ $t("fundBaseForm.assetClasses.help") }}</span>
                        </v-tooltip>
                      </template>
                    </v-autocomplete>

                    <v-text-field
                      v-if="displayField('assetClassesComment')"
                      v-model="formData.assetClassesComment"
                      :label="$t('fundBaseForm.assetClassesComment.label')"
                      required
                    >
                      <template #prepend>
                        <v-tooltip bottom max-width="300">
                          <template #activator="{ on }">
                            <v-icon color="primary" dark v-on="on">mdi-help</v-icon>
                          </template>
                          <span>{{ $t("fundBaseForm.assetClassesComment.help") }}</span>
                        </v-tooltip>
                      </template>
                    </v-text-field>

                    <v-text-field
                      v-if="displayField('assetManager')"
                      v-model="formData.assetManager"
                      :label="$t('fundBaseForm.assetManager.label')"
                      required
                    >
                      <template #prepend>
                        <v-tooltip bottom max-width="300">
                          <template #activator="{ on }">
                            <v-icon color="primary" dark v-on="on">mdi-help</v-icon>
                          </template>
                          <span>{{ $t("fundBaseForm.assetManager.help") }}</span>
                        </v-tooltip>
                      </template>
                    </v-text-field>

                    <v-text-field
                      v-if="displayField('dependance')"
                      v-model="formData.dependance"
                      :label="$t('fundBaseForm.dependance.label')"
                      required
                    >
                      <template #prepend>
                        <v-tooltip bottom max-width="300">
                          <template #activator="{ on }">
                            <v-icon color="primary" dark v-on="on">mdi-help</v-icon>
                          </template>
                          <span>{{ $t("fundBaseForm.dependance.help") }}</span>
                        </v-tooltip>
                      </template>
                    </v-text-field>

                    <v-combobox
                      v-if="displayField('isins')"
                      v-model="formData.isins"
                      :label="$t('fundBaseForm.isins.label')"
                      :hint="$t('fundBaseForm.isins.hint')"
                      :rules="[(v) => v.length > 0 || $t('validation.required')]"
                      multiple
                      small-chips
                      clearable
                      deletable-chips
                    >
                      <template #prepend>
                        <v-tooltip bottom max-width="300">
                          <template #activator="{ on }">
                            <v-icon color="primary" dark v-on="on">mdi-help</v-icon>
                          </template>
                          <span>{{ $t("fundBaseForm.isins.help") }}</span>
                        </v-tooltip>
                      </template>
                    </v-combobox>

                    <v-text-field
                      v-if="displayField('benchmark')"
                      v-model="formData.benchmark"
                      :label="$t('fundBaseForm.benchmark.label')"
                      required
                    >
                      <template #prepend>
                        <v-tooltip bottom max-width="300">
                          <template #activator="{ on }">
                            <v-icon color="primary" dark v-on="on">mdi-help</v-icon>
                          </template>
                          <span>{{ $t("fundBaseForm.benchmark.help") }}</span>
                        </v-tooltip>
                      </template>
                    </v-text-field>

                    <!-- the validation gets triggered due to v-ifs but the div solves it somehow -->
                    <div>
                      <v-autocomplete
                        v-if="displayField('investmentApproaches')"
                        v-model="formData.investmentApproaches"
                        :items="investmentApproaches"
                        :label="$t('fundBaseForm.investmentApproaches.label')"
                        :rules="[(v) => v.length > 0 || $t('validation.required')]"
                        multiple
                        small-chips
                        deletable-chips
                      >
                        <template #prepend>
                          <v-tooltip bottom max-width="300">
                            <template #activator="{ on }">
                              <v-icon color="primary" dark v-on="on">mdi-help</v-icon>
                            </template>
                            <span>{{ $t("fundBaseForm.investmentApproaches.help") }}</span>
                          </v-tooltip>
                        </template>
                      </v-autocomplete>
                    </div>

                    <v-select
                      v-if="displayField('fundType')"
                      :items="fundTypes"
                      :label="$t('fundBaseForm.fundType.label')"
                      v-model="formData.fundType"
                      :rules="[(v) => !!v || $t('validation.required')]"
                    >
                      <template #prepend>
                        <v-tooltip bottom max-width="300">
                          <template #activator="{ on }">
                            <v-icon color="primary" dark v-on="on">mdi-help</v-icon>
                          </template>
                          <span>{{ $t("fundBaseForm.fundType.help") }}</span>
                        </v-tooltip>
                      </template>
                    </v-select>

                    <v-checkbox
                      v-if="displayField('privateInvestors')"
                      v-model="formData.privateInvestors"
                      :label="$t('fundBaseForm.privateInvestors.label')"
                    >
                      <template #prepend>
                        <v-tooltip bottom max-width="300">
                          <template #activator="{ on }">
                            <v-icon color="primary" dark v-on="on">mdi-help</v-icon>
                          </template>
                          <span>{{ $t("fundBaseForm.privateInvestors.help") }}</span>
                        </v-tooltip>
                      </template>
                    </v-checkbox>

                    <v-autocomplete
                      v-if="displayField('tranches')"
                      v-model="formData.tranches"
                      :items="tranches"
                      :label="$t('fundBaseForm.tranches.label')"
                      multiple
                      small-chips
                      deletable-chips
                    >
                      <template #prepend>
                        <v-tooltip bottom max-width="300">
                          <template #activator="{ on }">
                            <v-icon color="primary" dark v-on="on">mdi-help</v-icon>
                          </template>
                          <span>{{ $t("fundBaseForm.tranches.help") }}</span>
                        </v-tooltip>
                      </template>
                    </v-autocomplete>

                    <v-text-field
                      v-if="displayField('tranchesComment')"
                      v-model="formData.tranchesComment"
                      :label="$t('fundBaseForm.tranchesComment.label')"
                      required
                    >
                      <template #prepend>
                        <v-tooltip bottom max-width="300">
                          <template #activator="{ on }">
                            <v-icon color="primary" dark v-on="on">mdi-help</v-icon>
                          </template>
                          <span>{{ $t("fundBaseForm.tranchesComment.help") }}</span>
                        </v-tooltip>
                      </template>
                    </v-text-field>

                    <v-checkbox
                      v-if="displayField('performanceFee')"
                      v-model="formData.performanceFee"
                      :label="$t('fundBaseForm.performanceFee.label')"
                    >
                      <template #prepend>
                        <v-tooltip bottom max-width="300">
                          <template #activator="{ on }">
                            <v-icon color="primary" dark v-on="on">mdi-help</v-icon>
                          </template>
                          <span>{{ $t("fundBaseForm.performanceFee.help") }}</span>
                        </v-tooltip>
                      </template>
                    </v-checkbox>

                    <v-checkbox
                      v-if="displayField('rter')"
                      v-model="formData.rter"
                      :label="$t('fundBaseForm.rter.label')"
                    >
                      <template #prepend>
                        <v-tooltip bottom max-width="300">
                          <template #activator="{ on }">
                            <v-icon color="primary" dark v-on="on">mdi-help</v-icon>
                          </template>
                          <span>{{ $t("fundBaseForm.rter.help") }}</span>
                        </v-tooltip>
                      </template>
                    </v-checkbox>
                  </v-col>

                  <v-col cols="12" md="6">
                    <v-autocomplete
                      v-if="displayField('salesCountries')"
                      v-model="formData.salesCountries"
                      :items="countries"
                      :label="$t('fundBaseForm.salesCountries.label')"
                      :hint="$t('fundBaseForm.salesCountries.hint')"
                      persistent-hint
                      :rules="[(v) => v.length > 0 || $t('validation.required')]"
                      multiple
                      small-chips
                      deletable-chips
                    >
                      <template #prepend>
                        <v-tooltip bottom max-width="300">
                          <template #activator="{ on }">
                            <v-icon color="primary" dark v-on="on">mdi-help</v-icon>
                          </template>
                          <span>{{ $t("fundBaseForm.salesCountries.help") }}</span>
                        </v-tooltip>
                      </template>
                    </v-autocomplete>

                    <v-select
                      v-if="displayField('investmentRegion')"
                      :items="investmentRegions"
                      :label="$t('fundBaseForm.investmentRegion.label')"
                      :rules="[(v) => !!v || $t('validation.required')]"
                      v-model="formData.investmentRegion"
                    >
                      <template #prepend>
                        <v-tooltip bottom max-width="300">
                          <template #activator="{ on }">
                            <v-icon color="primary" dark v-on="on">mdi-help</v-icon>
                          </template>
                          <span>{{ $t("fundBaseForm.investmentRegion.help") }}</span>
                        </v-tooltip>
                      </template>
                    </v-select>

                    <v-text-field
                      v-if="displayField('investmentRegionComment')"
                      :items="investmentRegions"
                      v-model="formData.investmentRegionComment"
                      :label="$t('fundBaseForm.investmentRegionComment.label')"
                      required
                    >
                      <template #prepend>
                        <v-tooltip bottom max-width="300">
                          <template #activator="{ on }">
                            <v-icon color="primary" dark v-on="on">mdi-help</v-icon>
                          </template>
                          <span>{{ $t("fundBaseForm.investmentRegionComment.help") }}</span>
                        </v-tooltip>
                      </template>
                    </v-text-field>

                    <v-text-field
                      v-if="displayField('volume')"
                      v-model="formData.volume"
                      :label="
                        $t('fundBaseForm.volume.label', {
                          year: new Date().getFullYear(),
                        })
                      "
                      :rules="[(v) => !!v || $t('validation.required')]"
                      required
                      type="number"
                    >
                      <template #prepend>
                        <v-tooltip bottom max-width="300">
                          <template #activator="{ on }">
                            <v-icon color="primary" dark v-on="on">mdi-help</v-icon>
                          </template>
                          <span>{{ $t("fundBaseForm.volume.help") }}</span>
                        </v-tooltip>
                      </template>
                    </v-text-field>

                    <v-autocomplete
                      v-if="displayField('currency')"
                      v-model="formData.currency"
                      :items="currencies"
                      :label="$t('fundBaseForm.currency.label')"
                      :rules="[(v) => !!v || $t('validation.required')]"
                    >
                      <template #prepend>
                        <v-tooltip bottom max-width="300">
                          <template #activator="{ on }">
                            <v-icon color="primary" dark v-on="on">mdi-help</v-icon>
                          </template>
                          <span>{{ $t("fundBaseForm.currency.help") }}</span>
                        </v-tooltip>
                      </template>
                    </v-autocomplete>

                    <v-menu
                      v-if="displayField('launchdate')"
                      ref="menu"
                      v-model="menu"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      offset-y
                      min-width="290px"
                    >
                      <template #activator="{ on }">
                        <v-text-field
                          v-model="formData.launchDate"
                          :label="$t('fundBaseForm.launchDate.label')"
                          :hint="$t('fundBaseForm.launchDate.hint')"
                          :rules="[(v) => !!v || $t('validation.required')]"
                          readonly
                          v-on="on"
                        >
                          <template #prepend>
                            <v-tooltip bottom max-width="300">
                              <template #activator="{ on }">
                                <v-icon color="primary" dark v-on="on">mdi-help</v-icon>
                              </template>
                              <span>{{ $t("fundBaseForm.launchDate.help") }}</span>
                            </v-tooltip>
                          </template>
                        </v-text-field>
                      </template>
                      <v-date-picker v-model="formData.launchDate" no-title scrollable @input="menu = false">
                      </v-date-picker>
                    </v-menu>

                    <v-select
                      v-if="displayField('language')"
                      :items="languages"
                      :label="$t('fundBaseForm.language.label')"
                      :rules="[(v) => !!v || $t('validation.required')]"
                      v-model="formData.language"
                    >
                      <template #prepend>
                        <v-tooltip bottom max-width="300">
                          <template #activator="{ on }">
                            <v-icon color="primary" dark v-on="on">mdi-help</v-icon>
                          </template>
                          <span>{{ $t("fundBaseForm.language.help") }}</span>
                        </v-tooltip>
                      </template>
                    </v-select>

                    <v-select
                      v-if="displayField('disclosure')"
                      v-model="formData.disclosure"
                      :items="disclosure"
                      :label="$t('fundBaseForm.disclosure.label')"
                      :rules="[(v) => !!v || $t('validation.required')]"
                    >
                      <template #prepend>
                        <v-tooltip bottom max-width="300">
                          <template #activator="{ on }">
                            <v-icon color="primary" dark v-on="on">mdi-help</v-icon>
                          </template>
                          <span>{{ $t("fundBaseForm.disclosure.help") }}</span>
                        </v-tooltip>
                      </template>
                    </v-select>

                    <v-textarea
                      v-if="displayField('disclosureComment')"
                      v-model="formData.disclosureComment"
                      :label="$t('fundBaseForm.disclosureComment.label')"
                      :hint="$t('fundBaseForm.disclosureComment.hint')"
                      :rules="[(v) => !v || v.length < 1000 || $t('validation.tooLong')]"
                      counter="1000"
                      auto-grow
                    >
                      <template #prepend>
                        <v-tooltip bottom max-width="300">
                          <template #activator="{ on }">
                            <v-icon color="primary" dark v-on="on">mdi-help</v-icon>
                          </template>
                          <span>{{ $t("fundBaseForm.disclosureComment.help") }}</span>
                        </v-tooltip>
                      </template>
                    </v-textarea>

                    <v-textarea
                      v-if="displayField('thirdPartyComment')"
                      :label="$t('fundBaseForm.thirdPartyComment.label')"
                      :hint="$t('fundBaseForm.thirdPartyComment.hint')"
                      v-model="formData.thirdPartyComment"
                      auto-grow
                    >
                      <template #prepend>
                        <v-tooltip bottom max-width="300">
                          <template #activator="{ on }">
                            <v-icon color="primary" dark v-on="on">mdi-help</v-icon>
                          </template>
                          <span>{{ $t("fundBaseForm.thirdPartyComment.help") }}</span>
                        </v-tooltip>
                      </template>
                    </v-textarea>

                    <v-textarea
                      v-if="displayField('comment')"
                      :label="$t('fundBaseForm.comment.label')"
                      v-model="formData.comment"
                      counter="200"
                      :rules="[(v) => !v || v.length < 200 || $t('validation.tooLong')]"
                      auto-grow
                    >
                      <template #prepend>
                        <v-tooltip bottom max-width="300">
                          <template #activator="{ on }">
                            <v-icon color="primary" dark v-on="on">mdi-help</v-icon>
                          </template>
                          <span>{{ $t("fundBaseForm.comment.help") }}</span>
                        </v-tooltip>
                      </template>
                    </v-textarea>
                  </v-col>
                </v-row>

                <v-row v-if="formData.assetManagement || formData.fof">
                  <v-col cols="12">
                    <v-alert type="info" dense>
                      {{ $t("fundBaseForm.infoContact") }}
                    </v-alert>
                  </v-col>
                </v-row>
              </v-card-text>

              <v-card-actions>
                <v-btn color="primary" @click="save" :loading="loading" :disabled="!valid" class="mr-2">
                  {{ $t("fundBaseForm.save") }}
                </v-btn>

                <v-dialog v-model="dialog" persistent max-width="600">
                  <template #activator="{ on, attrs }">
                    <v-btn :disabled="!valid" color="primary" v-bind="attrs" v-on="on">
                      {{ $t("fundBaseForm.finish") }}
                    </v-btn>
                  </template>
                  <v-card>
                    <v-card-title class="headline">
                      {{ $t("fundBaseForm.confirmation.title") }}
                    </v-card-title>
                    <v-card-text>
                      <Markdown :value="$t('fundBaseForm.confirmation.description')" />
                    </v-card-text>
                    <v-card-actions>
                      <v-spacer></v-spacer>
                      <v-btn color="warning" @click="dialog = false">
                        {{ $t("fundBaseForm.confirmation.cancel") }}
                      </v-btn>
                      <v-btn color="primary" @click="finish" :loading="loading">
                        {{ $t("fundBaseForm.confirmation.accept") }}
                      </v-btn>
                    </v-card-actions>
                  </v-card>
                </v-dialog>

                <v-spacer />

                <v-btn @click="step = 1" color="warning">
                  {{ $t("fundBaseForm.reset.label") }}
                </v-btn>

                <v-btn :to="{ name: 'home' }" color="secondary">
                  {{ $t("action.cancel") }}
                </v-btn>
              </v-card-actions>
            </v-card>
          </div>
        </v-stepper-content>
      </v-stepper-items>
    </v-stepper>
  </v-form>
</template>

<script>
import { pick, flatten, map, reject, propEq, compose, sortBy, prop, find, take, reverse, filter, pluck } from "ramda";

import tranches from "@fng/common/tranches";
import countries from "@fng/common/countries";
import fundTypes from "@fng/common/fundTypes";
import disclosure from "@fng/common/disclosure";
import currencies from "@fng/common/currencies";
import assetClasses from "@fng/common/assetClasses";
import generateSelectItems from "@/utils/generateSelectItems";
import investmentRegions from "@fng/common/investmentRegions";
import FundBaseFormQuery from "@/gql/queries/FundBaseFormQuery.gql";
import investmentApproaches from "@fng/common/investmentApproaches";
import FundBaseFormMutation from "@/gql/mutations/FundBaseFormMutation.gql";
import AddRequirementsToFundMutation from "@/gql/mutations/AddRequirementsToFundMutation.gql";
import OptionsQuery from "@/gql/queries/auditor/OptionsQuery.gql";

import { displayFundField } from "@fng/common/fund/displayFundField";

const languages = ["en", "de"];

const fields = [
  "name",
  "assetManagement",
  "fof",
  "assetClasses",
  "assetClassesComment",
  "assetManager",
  "dependance",
  "isins",
  "privateInvestors",
  "benchmark",
  "investmentApproaches",
  "fundType",
  "salesCountries",
  "investmentRegion",
  "investmentRegionComment",
  "volume",
  "currency",
  "launchDate",
  "tranches",
  "tranchesComment",
  "performanceFee",
  "rter",
  "disclosure",
  "language",
  "referenceFund",
  "disclosureComment",
  "thirdPartyComment",
  "comment",
];

export default {
  props: {
    id: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      formData: null,
      fund: null,
      valid: false,
      loading: false,
      menu: false,
      dialog: false,
      noReference: false,
      filled: false,
      step: 1,
      referenceType: "old",
      options: null,
    };
  },
  computed: {
    assetClasses: generateSelectItems(assetClasses, "fund.assetClasses"),
    investmentApproaches: generateSelectItems(investmentApproaches, "fund.investmentApproaches"),
    fundTypes: generateSelectItems(fundTypes, "fund.fundTypes"),
    assetManagement: generateSelectItems([false, true], "fund.assetManagement"),
    fof: generateSelectItems([false, true], "fund.fof"),
    tranches: generateSelectItems(tranches, "fund.tranches"),
    disclosure() {
      return sortBy(prop("text"), generateSelectItems(disclosure, "disclosure").apply(this));
    },
    investmentRegions: generateSelectItems(investmentRegions, "fund.investmentRegions"),
    countries() {
      return sortBy(prop("text"), generateSelectItems(countries, "countries").apply(this));
    },
    currencies() {
      return sortBy(prop("text"), generateSelectItems(currencies, "currencies").apply(this));
    },
    languages() {
      return sortBy(prop("text"), generateSelectItems(languages, "languages").apply(this));
    },
    allFunds() {
      const periods = map(
        ({ year, funds }) => {
          return map((fund) => {
            return {
              ...fund,
              year,
            };
          }, funds);
        },
        [...this.fund.user.auditPeriods, ...flatten(pluck("auditPeriods", this.fund.user.referenceableUsers))]
      );
      return compose(reject(propEq("state", "NEW")), reject(propEq("id", this.fund.id)), flatten)(periods);
    },
    allFundsOptions() {
      return reverse(
        map(
          ({ id, name, year }) => ({
            value: id,
            text: `${year} - ${name}`,
            year,
          }),
          this.allFunds
        )
      );
    },
    currentFundsOptions() {
      return filter(({ year }) => year === new Date().getFullYear(), this.allFundsOptions);
    },
    oldFundsOptions() {
      return reverse(
        sortBy(
          prop("year"),
          filter(({ year }) => year < new Date().getFullYear(), this.allFundsOptions)
        )
      );
    },
    fundsOptions() {
      return this.referenceType === "old" ? this.oldFundsOptions : this.currentFundsOptions;
    },
    referenceFundObject() {
      return this.formData.referenceFund
        ? find((fund) => fund.id === this.formData.referenceFund, this.allFunds)
        : null;
    },
    mayStartEdit() {
      const startEdit = this.options?.find((option) => option.key === "startEdit")?.value;

      // If startEdit is not set, the user cannot start the edit
      if (!startEdit) {
        return false;
      }

      const startEditDate = new Date(startEdit);
      const now = new Date();

      // If the startEdit date is in the previous year, the user cannot start the edit
      if (startEditDate.getFullYear() < this.fund.auditPeriod.year) {
        return false;
      }

      return now > startEditDate;
    },
  },
  apollo: {
    fund: {
      query: FundBaseFormQuery,
      variables() {
        return {
          id: this.id,
        };
      },
    },
    options: {
      query: OptionsQuery,
      fetchPolicy: "cache-and-network",
    },
  },
  watch: {
    fund(val) {
      this.formData = {
        ...pick(fields, val),
        referenceFund: val.referenceFund ? val.referenceFund.id : null,
        launchDate: val.launchDate ? val.launchDate.substr(0, 10) : null,
      };
      // no fund was referenced but data was filled, set no reference
      if (!this.formData.referenceFund && val.createdAt !== val.updatedAt) {
        this.noReference = true;
        this.referenceType = "none";
        this.step = 3;
      }

      if (this.formData.referenceFund && val.createdAt !== val.updatedAt) {
        this.noReference = false;
        this.step = 3;
      }
      // no funds to reference, set no reference
      // if (this.allFunds.length === 0)
      //   this.noReference = true;
    },
  },
  methods: {
    displayField(field) {
      return displayFundField(this.formData, field);
    },
    async save() {
      if (!this.$refs.form.validate()) return;
      this.loading = true;
      await this.update();
      this.loading = false;
      this.$router.push({ name: "home" });
    },
    async finish() {
      if (!this.$refs.form.validate()) return;
      this.loading = true;
      await this.update();
      await this.finalize();
      this.loading = false;
      this.$router.push({
        name: "userFundMinimal",
        params: {
          id: this.id,
        },
      });
    },
    async update() {
      await this.$apollo.mutate({
        mutation: FundBaseFormMutation,
        variables: {
          id: this.fund.id,
          input: {
            ...this.formData,
            volume: parseFloat(this.formData.volume),
          },
        },
      });
    },
    async finalize() {
      await this.$apollo.mutate({
        mutation: AddRequirementsToFundMutation,
        variables: {
          id: this.fund.id,
        },
      });
    },
    fill() {
      this.formData = {
        ...this.formData,
        ...pick(fields, this.referenceFundObject),
        referenceFund: this.formData.referenceFund,
        launchDate: take(10, this.referenceFundObject.launchDate), // only take date, not time
      };
      this.filled = true;
    },
  },
};
</script>
